import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AUTH } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { ShieldCheck } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const u = await login(email, password);
      if (u.role === "super_admin") nav("/admin");
      else if (u.role === "company") nav("/company");
      else nav("/staff");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 py-16">
      <div className="text-center mb-6">
        <div className="inline-flex w-14 h-14 rounded-md bg-brand items-center justify-center mb-4">
          <ShieldCheck className="w-7 h-7 text-white" strokeWidth={2.4} />
        </div>
        <h1 className="text-3xl font-outfit font-bold text-white">Secure Login</h1>
        <p className="text-white/60 text-sm mt-1 font-manrope">Staff, companies, and admin.</p>
      </div>

      <form onSubmit={submit} className="card-flat p-6 space-y-4">
        <div>
          <Label className="font-manrope">Email</Label>
          <Input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value.trim().toLowerCase())}
            data-testid={AUTH.loginEmail}
            placeholder="you@example.com"
            autoCapitalize="none"
            autoCorrect="off"
            autoComplete="email"
            spellCheck="false"
            inputMode="email"
          />
        </div>
        <div>
          <Label className="font-manrope">Password</Label>
          <Input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid={AUTH.loginPassword}
            placeholder="••••••••"
            autoCapitalize="none"
            autoCorrect="off"
            autoComplete="current-password"
            spellCheck="false"
          />
        </div>
        {error && <div className="text-sm text-red-600 font-manrope" data-testid={AUTH.loginError}>{error}</div>}
        <Button type="submit" disabled={busy} className="w-full bg-brand hover:bg-brand-hover text-white py-6 font-manrope font-semibold" data-testid={AUTH.loginSubmit}>
          {busy ? "Signing in…" : "Sign in"}
        </Button>

        <div className="text-center text-sm font-manrope">
          <Link to="/forgot-password" className="text-brand font-semibold" data-testid="login-forgot-link">Forgot your password?</Link>
        </div>

        <div className="text-center text-sm text-white/70 font-manrope pt-4 border-t border-white/10">
          New to SecuSource?{" "}
          <Link to="/register/staff" className="text-brand font-semibold">Register as staff</Link>
          {" · "}
          <Link to="/register/company" className="text-brand font-semibold">Register as company</Link>
        </div>
      </form>
    </div>
  );
}
